// src/authpages/timetables/GoogleCalendarConnect.js
import React, { useState, useEffect } from 'react';
import { Button, Alert } from 'react-bootstrap';
import { doc, setDoc, getDoc } from '../../firebase';
import { db, auth } from '../../firebase';
import { 
  GoogleAuthProvider, 
  signInWithPopup
} from 'firebase/auth';
import { initGoogleApiClient, fetchGoogleCalendarEvents } from './GoogleCalendarUtils';

const GoogleCalendarConnect = ({ onCalendarEvents }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [events, setEvents] = useState([]);
  const [lastSynced, setLastSynced] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [useRealApi, setUseRealApi] = useState(true);

  // Check connection status on component mount
  useEffect(() => {
    const checkConnectionStatus = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          setLoading(false);
          return;
        }

        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists() && userDoc.data().googleCalendarConnected) {
          setIsConnected(true);
          
          if (userDoc.data().lastSyncedAt) {
            setLastSynced(userDoc.data().lastSyncedAt.toDate());
          }

          // If we have a stored access token, try to use it
          if (userDoc.data().googleAccessToken) {
            setAccessToken(userDoc.data().googleAccessToken);
            try {
              await fetchUserCalendarEvents(userDoc.data().googleAccessToken);
            } catch (err) {
              console.error('Error fetching events with stored token:', err);
              // If API fetch fails, fall back to sample data
              setUseRealApi(false);
              const sampleEvents = generateSampleEvents();
              setEvents(sampleEvents);
              if (onCalendarEvents) {
                onCalendarEvents(sampleEvents);
              }
            }
          } else {
            // No token, use sample data
            setUseRealApi(false);
            const sampleEvents = generateSampleEvents();
            setEvents(sampleEvents);
            if (onCalendarEvents) {
              onCalendarEvents(sampleEvents);
            }
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error checking connection status:', err);
        setError('Error checking connection status: ' + err.message);
        setLoading(false);
        // Fall back to sample data
        setUseRealApi(false);
      }
    };
    
    checkConnectionStatus();
  }, [onCalendarEvents]);

  // Fetch user's actual calendar events
  const fetchUserCalendarEvents = async (token) => {
    if (!useRealApi) {
      const sampleEvents = generateSampleEvents();
      setEvents(sampleEvents);
      if (onCalendarEvents) {
        onCalendarEvents(sampleEvents);
      }
      return sampleEvents;
    }

    try {
      // Initialize Google API client
      const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
      const clientInitialized = await initGoogleApiClient(apiKey);
      
      if (!clientInitialized) {
        throw new Error('Failed to initialize Google API client');
      }
      
      // Fetch events using the token
      const calendarEvents = await fetchGoogleCalendarEvents(token, 30); // Fetch events for the next 30 days
      
      // Update state
      setEvents(calendarEvents);
      
      // Pass events to parent component
      if (onCalendarEvents) {
        onCalendarEvents(calendarEvents);
      }
      
      return calendarEvents;
    } catch (err) {
      console.error('Error fetching calendar events:', err);
      // If real API fails, fall back to sample data
      setUseRealApi(false);
      const sampleEvents = generateSampleEvents();
      setEvents(sampleEvents);
      if (onCalendarEvents) {
        onCalendarEvents(sampleEvents);
      }
      return sampleEvents;
    }
  };

  // Connect to Google Calendar
  const connectGoogleCalendar = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Create Google Auth Provider
      const provider = new GoogleAuthProvider();
      
      // Add required scopes for Google Calendar
      provider.addScope('https://www.googleapis.com/auth/calendar.readonly');
      
      try {
        // Trigger Google Sign-In popup
        const result = await signInWithPopup(auth, provider);
        
        // Get the OAuth access token
        const credential = GoogleAuthProvider.credentialFromResult(result);
        const token = credential.accessToken;
        setAccessToken(token);
        
        const user = auth.currentUser;
        const userDocRef = doc(db, 'users', user.uid);
        
        // Get current time for last synced
        const now = new Date();
        
        // Try to fetch actual calendar events
        try {
          if (useRealApi) {
            await fetchUserCalendarEvents(token);
          } else {
            // If not using real API, use sample events
            const sampleEvents = generateSampleEvents();
            setEvents(sampleEvents);
            if (onCalendarEvents) {
              onCalendarEvents(sampleEvents);
            }
          }
        } catch (fetchErr) {
          console.error('Error fetching calendar events after connect:', fetchErr);
          setError('Connected but failed to fetch calendar events: ' + fetchErr.message);
          // Fall back to sample data
          setUseRealApi(false);
          const sampleEvents = generateSampleEvents();
          setEvents(sampleEvents);
          if (onCalendarEvents) {
            onCalendarEvents(sampleEvents);
          }
        }
        
        // Store connection status and token in Firebase
        await setDoc(userDocRef, {
          googleCalendarConnected: true,
          googleAccessToken: token,
          lastSyncedAt: now
        }, { merge: true });
        
        setIsConnected(true);
        setLastSynced(now);
        
        setLoading(false);
      } catch (error) {
        console.error('Google Sign-In Error:', error);
        
        if (error.code === 'auth/popup-blocked') {
          setError('Popup blocked. Please enable popups for this site.');
        } else if (error.code === 'auth/popup-closed-by-user') {
          setError('Sign-in popup was closed before completion.');
        } else {
          setError('Failed to connect to Google Calendar: ' + error.message);
        }
        
        setLoading(false);
      }
    } catch (err) {
      console.error('Error connecting to Google Calendar:', err);
      setError('An unexpected error occurred: ' + err.message);
      setLoading(false);
    }
  };

  // Disconnect Google Calendar
  const disconnectGoogleCalendar = async () => {
    try {
      setLoading(true);
      
      const user = auth.currentUser;
      if (!user) {
        setError('You must be logged in to disconnect Google Calendar');
        setLoading(false);
        return;
      }
      
      // Update user profile to indicate Google Calendar is disconnected
      const userDocRef = doc(db, 'users', user.uid);
      await setDoc(userDocRef, {
        googleCalendarConnected: false,
        googleAccessToken: null
      }, { merge: true });
      
      setIsConnected(false);
      setEvents([]);
      setAccessToken(null);
      
      // Pass empty events array back to parent
      if (onCalendarEvents) {
        onCalendarEvents([]);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error disconnecting from Google Calendar:', err);
      setError('Failed to disconnect from Google Calendar: ' + err.message);
      setLoading(false);
    }
  };

  // Sync now button handler - fetch fresh calendar data
  const handleSync = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let calendarEvents;
      
      if (useRealApi && accessToken) {
        try {
          calendarEvents = await fetchUserCalendarEvents(accessToken);
        } catch (err) {
          console.error('Error fetching real calendar events during sync:', err);
          // Fall back to sample data
          setUseRealApi(false);
          calendarEvents = generateSampleEvents();
          setEvents(calendarEvents);
          if (onCalendarEvents) {
            onCalendarEvents(calendarEvents);
          }
        }
      } else {
        // Use sample data if no API access or no token
        calendarEvents = generateSampleEvents();
        setEvents(calendarEvents);
        if (onCalendarEvents) {
          onCalendarEvents(calendarEvents);
        }
      }
      
      // Update lastSynced time
      const now = new Date();
      setLastSynced(now);
      
      // Store lastSynced time in Firebase
      const user = auth.currentUser;
      if (user) {
        const userDocRef = doc(db, 'users', user.uid);
        await setDoc(userDocRef, {
          lastSyncedAt: now
        }, { merge: true });
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error syncing calendar:', err);
      setError('Failed to sync with Google Calendar: ' + err.message);
      setLoading(false);
    }
  };

  // Generate sample events - we're keeping this as a fallback
  const generateSampleEvents = () => {
    const events = [];
    const today = new Date();
    
    // Sample class events
    const classes = [
      { title: 'Calculus Lecture', color: '#4285F4' },
      { title: 'Computer Science Lab', color: '#0F9D58' },
      { title: 'Physics Seminar', color: '#F4B400' },
      { title: 'English Literature', color: '#DB4437' },
      { title: 'Study Group', color: '#673AB7' }
    ];
    
    // Generate recurring events for a week
    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() + i);
      
      // Don't add events on weekend
      if (date.getDay() !== 0 && date.getDay() !== 6) {
        // Morning class
        const morningClass = classes[i % classes.length];
        const morningStart = new Date(date);
        morningStart.setHours(9, 0, 0);
        const morningEnd = new Date(date);
        morningEnd.setHours(10, 30, 0);
        
        events.push({
          id: `google-morning-${i}`,
          title: morningClass.title,
          start: morningStart,
          end: morningEnd,
          provider: 'Google Calendar',
          color: morningClass.color
        });
        
        // Afternoon class
        const afternoonClass = classes[(i + 2) % classes.length];
        const afternoonStart = new Date(date);
        afternoonStart.setHours(14, 0, 0);
        const afternoonEnd = new Date(date);
        afternoonEnd.setHours(15, 30, 0);
        
        events.push({
          id: `google-afternoon-${i}`,
          title: afternoonClass.title,
          start: afternoonStart,
          end: afternoonEnd,
          provider: 'Google Calendar',
          color: afternoonClass.color
        });
      }
    }
    
    // Add a few one-time events
    const eventTypes = [
      { title: 'Project Meeting', color: '#4285F4' },
      { title: 'Office Hours', color: '#0F9D58' },
      { title: 'Research Presentation', color: '#F4B400' },
      { title: 'Exam', color: '#DB4437' },
      { title: 'Group Discussion', color: '#673AB7' }
    ];
    
    for (let i = 0; i < 5; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() + Math.floor(Math.random() * 7));
      
      const event = eventTypes[i];
      const startHour = 10 + Math.floor(Math.random() * 6);
      
      const start = new Date(date);
      start.setHours(startHour, 0, 0);
      
      const end = new Date(date);
      end.setHours(startHour + 1, 30, 0);
      
      events.push({
        id: `google-event-${i}`,
        title: event.title,
        start: start,
        end: end,
        provider: 'Google Calendar',
        color: event.color
      });
    }
    
    return events;
  };

  if (loading) {
    return (
      <div className="text-center">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <>
      {error && <Alert variant="danger">{error}</Alert>}
      
      {isConnected ? (
        <div>
          <div className="mb-4">
            <Alert variant="success" className="mb-2">
              Google Calendar Connected
              {!useRealApi && (
                <span className="small d-block mt-1">
                  (Using sample data - API connection unavailable)
                </span>
              )}
            </Alert>
            {events.length > 0 && (
              <p>
                <strong>{events.length}</strong> events imported
                {lastSynced && (
                  <span className="text-muted ms-2">
                    (Last synced: {lastSynced.toLocaleString()})
                  </span>
                )}
              </p>
            )}
          </div>
          
          <div className="d-flex justify-content-between">
            <Button 
              variant="outline-primary"
              onClick={handleSync}
              disabled={loading}
              className="me-2"
            >
              Sync Now
            </Button>
            
            <Button 
              variant="outline-danger"
              onClick={disconnectGoogleCalendar}
              disabled={loading}
            >
              Disconnect
            </Button>
          </div>
        </div>
      ) : (
        <div className="text-center">
          <Button 
            variant="primary" 
            className="w-100 py-2"
            onClick={connectGoogleCalendar}
            disabled={loading}
          >
            Connect Google Calendar
          </Button>
        </div>
      )}
    </>
  );
};

export default GoogleCalendarConnect;